// @flow
/** @jsx jsx */

import * as React from 'react';

import { Duration } from '@zapier/theme';
import {
  border,
  borderRadius,
} from '@zapier/common-components/src/components/Tabs/constants';
import { css, jsx, keyframes } from '@emotion/core';

import { type IconNameType } from '@zapier/common-components/src/components/Icon';

type TabPropTypes = {
  /** Tab contents */
  children?: React.Node,
  /** Icon left of title */
  icon?: IconNameType,
  /** unique tab id (auto-generated when inside of Tabs) */
  id?: string,
  /** tab will show when isActive is true (auto-generated when inside of Tabs) */
  isActive?: boolean,
  /** Tab title string */
  title: string,
};

const enter = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const tabBaseCss = css({
  border,
  borderBottomLeftRadius: borderRadius,
  borderBottomRightRadius: borderRadius,
  borderTop: 'none',
  overflow: 'hidden',
  display: 'none',
});

const tabIsActiveCss = css({
  display: 'block',
});

const innerCss = css({
  animation: `${enter} ${Duration.medium} ease-in-out`,
});

/**
 * For use inside of the `Tabs` component
 */
const Tab = ({ children, id, isActive = false }: TabPropTypes) => {
  const tabCss = css(tabBaseCss, isActive && tabIsActiveCss);

  return (
    <div
      aria-labelledby={id ? `${id}-title` : null}
      css={tabCss}
      id={id}
      role="tabpanel"
    >
      <div css={innerCss}>{children}</div>
    </div>
  );
};

export default Tab;
